import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { getIn } from 'formik'

import MinimisedSection from '../../../../../../../../../components/MinimisedSection'
import MinimisedSectionHeader from '../../../../../../../../../components/MinimisedSection/MinimisedSectionHeader'
import Field from '../../../../../../../../../components/Form/Field'
import FieldLabelWrapper from '../../../../../../../../../features/components/Form/FieldLabelWrapper'
import StakeholderMetricsFields from '../../../../StakeholderMetricsFields'

import { getSroiCurrencySymbol } from '../../../../../../hooks'

import {
  AVERAGE_ANNUAL_COST,
  SPORTS_TYPE,
  sportsTypeOptions,
  STAKEHOLDER_COUNT,
  STAKEHOLDER_TYPE,
  STAKEHOLDERS_LIST,
  stakeholderTypeOptions
} from '../../../../../stakeholdersFields'
import { COUNTRY_ID } from '../../../../../fields'

import useStyles from './styles'

const StakeholderSection = ({ formik, index, isSelected, onSelect, onClose, onDelete }) => {
  const { t } = useTranslation()

  const classes = useStyles()

  const { values } = formik

  const countryOfOperation = values[COUNTRY_ID]

  const currencySymbol = getSroiCurrencySymbol(countryOfOperation)

  const stakeholderSectionTitle = useMemo(() => {
    const stakeholderType = getIn(values, `${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_TYPE}]`)
    const stakeholderLabel = stakeholderTypeOptions.find(option => option.value === stakeholderType)?.label
    return stakeholderLabel || t(`Stakeholder ${index + 1}`)
  }, [values, index, t])

  const onClickHandler = useCallback(() => {
    if (isSelected) {
      onClose()
    } else {
      onSelect()
    }
  }, [isSelected, onSelect, onClose])

  return (
    <MinimisedSection
      headerContent={<MinimisedSectionHeader title={stakeholderSectionTitle} onDelete={onDelete} />}
      isOpened={isSelected}
      onHeaderClick={onClickHandler}
    >
      <FieldLabelWrapper label={t('Sports Type')} className={classes.sportsTypeFieldWrapper}>
        <Field
          name={`${STAKEHOLDERS_LIST}[${index}][${SPORTS_TYPE}]`}
          formik={formik}
          options={sportsTypeOptions}
          placeholder="Sports Type"
        />
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('Stakeholder Type')}>
        <Field
          name={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_TYPE}]`}
          formik={formik}
          options={stakeholderTypeOptions}
          placeholder="Stakeholder Type"
        />
      </FieldLabelWrapper>

      <FieldLabelWrapper
        label={t('Stakeholder Count')}
        labelFor={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_COUNT}]`}
      >
        <Field
          placeholder={t('Stakeholder Count')}
          formik={formik}
          name={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_COUNT}]`}
          type="number"
        />
      </FieldLabelWrapper>

      <FieldLabelWrapper
        label={t('Average Annual Cost')}
        labelFor={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_ANNUAL_COST}]`}
      >
        <Field
          placeholder={t('Average Annual Cost')}
          formik={formik}
          name={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_ANNUAL_COST}]`}
          symbol={currencySymbol}
          type="number"
        />
      </FieldLabelWrapper>

      <StakeholderMetricsFields formik={formik} index={index} />
    </MinimisedSection>
  )
}

StakeholderSection.propTypes = {
  formik: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

export default StakeholderSection
